import type { ApplyMatchesResult, ProductMatchResult } from '../../services/integrationApi';

type ScanMatch = ProductMatchResult['matched'][number];

/**
 * Products an Apply adds to the sync selection before linking: matches outside it that no catalog
 * gate excludes (MOT-408). A gated match is still sent to `applyMatches`, which skips it and
 * reports why; adding it first would leave it selected but never linked or pushed.
 */
export function selectionAddsFor(matches: ScanMatch[]): string[] {
  return matches.filter((m) => m.inSelection === false && !m.catalogGate).map((m) => m.canonicalProductId);
}

/**
 * Every match of the last scan, as Apply needs it. The preview on screen can predate the gate flag
 * (a result read from the integration doc carries none), so whenever a match might be added to the
 * selection the list is read from the scan report, whose pages carry each row's current gate. A
 * report with no stored pages (a scan from before pages existed) falls back to `shown`.
 */
export async function loadScanMatchesForApply(
  integrationId: string,
  shown: ScanMatch[],
  total: number,
  getPage: (integrationId: string, page: number) => Promise<{ rows: ScanMatch[]; pages: number }>,
): Promise<ScanMatch[]> {
  const mayAdd = shown.some((m) => m.inSelection === false);
  if (shown.length >= total && !mayAdd) return shown;
  const rows: ScanMatch[] = [];
  for (let page = 0; ; page++) {
    const next = await getPage(integrationId, page);
    rows.push(...next.rows);
    if (page + 1 >= next.pages) break;
  }
  return rows.length > 0 ? rows : shown;
}

export const emptyApplyMatchesResult = (): ApplyMatchesResult => ({
  linked: 0,
  enriched: 0,
  overridden: 0,
  errors: 0,
  skipped: 0,
  skips: [],
  updateFailures: [],
  alreadyLinked: 0,
});

/** Sums the batches of one Apply click (an older API without `skipped` / `skips` counts as none). */
export function addApplyMatchesResult(total: ApplyMatchesResult, part: Partial<ApplyMatchesResult>): ApplyMatchesResult {
  return {
    linked: total.linked + (part.linked ?? 0),
    enriched: total.enriched + (part.enriched ?? 0),
    overridden: total.overridden + (part.overridden ?? 0),
    errors: total.errors + (part.errors ?? 0),
    skipped: total.skipped + (part.skipped ?? 0),
    skips: [...total.skips, ...(part.skips ?? [])],
    updateFailures: [...(total.updateFailures ?? []), ...(part.updateFailures ?? [])],
    alreadyLinked: (total.alreadyLinked ?? 0) + (part.alreadyLinked ?? 0),
  };
}

/** The skips grouped by code, in first-seen order, with how many products each code covers. */
export function groupApplySkips(skips: ApplyMatchesResult['skips']): Array<{ code: string; count: number }> {
  const counts = new Map<string, number>();
  for (const skip of skips) counts.set(skip.code, (counts.get(skip.code) ?? 0) + 1);
  return Array.from(counts, ([code, count]) => ({ code, count }));
}
